import type { FitScoreLevel } from './product-fit-score-service-utils'

export type FitScoreSkillKey = 'professionalRole' | 'seniority' | 'language'

export interface FitScoreSkillCompatibility {
  compatibility: FitScoreLevel
}

export interface FitScoreProfessionalRoleCompatibility extends FitScoreSkillCompatibility {
  currentProfessionalRole: string
}

export interface FitScoreSeniorityCompatibility extends FitScoreSkillCompatibility {
  requiredSeniority: string
  currentSeniority: string
}

// Par idioma + proficiência usado pelo backend para registrar tanto o idioma do curso
// quanto o do usuário considerados no cálculo de compatibilidade.
// `proficiency` vem como string em PT ("nativo", "básico", "avançado") por enquanto —
// quando o backend mudar pra código (NATIVE, BASIC, ADVANCED), traduzir via i18n.
export interface FitScoreLanguageEntry {
  language: string
  proficiency: string
}

export interface FitScoreLanguageCompatibility extends FitScoreSkillCompatibility {
  requiredLanguage: {
    caption: string[]
    main: string[]
    // Idioma do curso usado no cálculo. Vira null quando o user não tem match
    // com nenhum dos idiomas do curso — nesse caso usamos main[] pra montar a frase
    considered: FitScoreLanguageEntry | null
  }
  currentLanguage: {
    userList: FitScoreLanguageEntry[]
    // Idioma do usuário usado no cálculo (sempre populado — vem o idioma do user
    // mesmo quando não bate com o curso)
    considered: FitScoreLanguageEntry
  }
}

export interface FitScoreSkillsCompatibility {
  professionalRole: FitScoreProfessionalRoleCompatibility
  seniority: FitScoreSeniorityCompatibility
  language: FitScoreLanguageCompatibility
}

// Resposta crua da API
export interface FitScoreResponse {
  profileCompatibility: FitScoreLevel
  skillsCompatibility: FitScoreSkillsCompatibility
  // Só vem populado em LOW aprovado manualmente pelo CX. HIGH sempre null
  approved: boolean | null
}

// Formato compact usado em listas (search, recommended, summary) — só os campos
// essenciais pra UI/analytics sem carregar o skillsCompatibility completo.
export interface FitScoreSummary {
  level: FitScoreLevel
  approved: boolean | null
}

export interface ProductFitScoreResponse {
  fitScore: FitScoreResponse
}

// Skill já filtrada e enriquecida pelo formatter (vem em ordem canônica e só incompatíveis)
export interface FormattedFitScoreSkill {
  key: FitScoreSkillKey
  data: FitScoreSkillCompatibility
}

// Estrutura pronta para consumo na render — produzida pelo formatter no loader
export interface FormattedFitScore {
  profileCompatibility: FitScoreLevel
  skillsCompatibility: FitScoreSkillsCompatibility
  // Nível efetivo aplicado pela UI: HIGH sempre que approved=true, senão o original
  effectiveLevel: FitScoreLevel
  // CSS var pronta para uso em `sx={{ color: levelColor }}`
  levelColor: string
  // Lista de skills com compatibility != HIGH (já filtrada e na ordem canônica)
  incompatibleSkills: FormattedFitScoreSkill[]
  // true quando o CX aprovou manualmente um perfil LOW/MEDIUM. Trata-se como HIGH na UI
  approved: boolean | null
  // approved === true (boolean estrito pra usar em condicionais)
  isApproved: boolean
  // effectiveLevel === HIGH (boolean estrito pra usar em condicionais)
  isHigh: boolean
  // true quando effectiveLevel é LOW ou MEDIUM — controla exibição da seção de recomendações
  showRecommendations: boolean
  // Chave i18n da descrição do bloco fit-score, considerando approved/level
  descriptionI18nKey: string
}

export const PRODUCT_FIT_SCORE_QUERY_KEY = 'product-fit-score'
